/** Team & invites page (pages/team.tsx) — member management UI copy. */
export const team = {
  page: {
    title: "Team",
    description:
      "Users, permissions, and invites. Billing remains with the workspace creator.",
  },
  noPermission: "You do not have permission to manage team settings.",
  tabs: {
    members: "Users",
    groups: "Permissions",
  },
  memberFallback: "Member",
  badge: {
    creator: "Creator",
    handoff: "Operational handoff",
    starter: "Starter",
  },
  label: {
    locations: "Locations",
    devices: "Devices",
    channels: "Channels",
  },
  field: {
    permissionGroup: "Permission group",
  },
  action: {
    editScope: "Edit scope",
    operationalHandoff: "Operational handoff",
    copyLink: "Copy link",
    sendEmail: "Send email",
    revoke: "Revoke",
    duplicate: "Duplicate",
    invite: "Invite",
  },
  confirm: {
    removeMember: "Remove this member from the workspace?",
    handoff:
      "Grant this member creator-level operational permissions? Billing stays on the workspace creator.",
    deleteGroup:
      'Delete "{name}"? Members using this group will lose their assignment.',
  },
  members: {
    title: "Users",
    description:
      "Assign a permission group per member. The workspace creator has full access and billing ownership. Scope can narrow group defaults for other members.",
    error: {
      title: "Couldn't load members",
      description:
        "Something went wrong while loading your team members. Check your connection and try again.",
    },
  },
  invites: {
    title: "Pending invites",
    description:
      "Copy the invite link to test without email delivery. Recipients must sign in with the invited email to accept.",
    groupPrefix: "Group:",
    empty: "No pending invites.",
    emptyState: {
      title: "No pending invites",
      description: "Invite a teammate to give them access to this workspace.",
      action: "Invite a teammate",
    },
    error: {
      title: "Couldn't load invites",
      description:
        "Something went wrong while loading pending invites. Check your connection and try again.",
    },
  },
  inviteDialog: {
    title: "Invite teammate",
    description:
      "Creates an invite and copies the accept link. Email is attempted when configured; otherwise share the link directly.",
    noGroupsTitle: "No permission groups",
    noGroupsBody:
      "You need at least one permission group before inviting teammates.",
    goToGroups: "Go to Groups",
    createInvite: "Create invite",
  },
  groups: {
    title: "Permission groups",
    description:
      "Reusable capability templates. Members are assigned one group. Starter groups can be duplicated to customize.",
    newGroup: "New group",
    emptyState: {
      title: "No permission groups yet",
      description:
        "Create a permission group to define what teammates can do, then assign it when you invite them.",
    },
    error: {
      title: "Couldn't load permission groups",
      description:
        "Something went wrong while loading permission groups. Check your connection and try again.",
    },
  },
  groupForm: {
    createTitle: "Create permission group",
    editTitle: "Edit permission group",
    duplicateTitle: "Duplicate permission group",
    description:
      "Capabilities define what members can do. Default scope narrows access for control-only groups.",
    copySuffix: "{name} (copy)",
    capabilities: "Capabilities",
    defaultScope: "Default scope",
    defaultScopeHint:
      "Optional. Empty means workspace-wide. Required for control-only groups.",
    noneYet: "None yet",
  },
  scope: {
    title: "Scope for {name}",
    description:
      "Narrows the permission group. Empty = workspace-wide (except control-only groups).",
  },
  validation: {
    nameRequired: "Name is required.",
    capabilityRequired: "Select at least one capability.",
    controlScopeRequired:
      "Control-only groups need at least one default location or device.",
  },
  toast: {
    groupUpdated: "Permission group updated.",
    groupUpdateFailed: "Failed to update group",
    groupCreated: "Permission group created.",
    groupCreateFailed: "Failed to create group",
    groupDeleteFailed: "Failed to delete group",
    memberRemoved: "Member removed.",
    memberRemoveFailed: "Failed to remove member",
    handoffSuccess: "Operational permissions transferred.",
    handoffFailed: "Handoff failed",
    inviteLinkCopied: "Invite link copied",
    inviteEmailSent: "Invite email sent",
    inviteEmailNotSent: "Email not sent, link copied",
    inviteEmailNotSentDescription:
      "Without a verified domain, use the copied link instead.",
    inviteCreatedTitle: "Invite created, link copied",
    inviteCreatedDescription:
      "Share the link with the invitee. Email only works with a verified domain or your Resend account email.",
    inviteCreateFailed: "Failed to create invite",
    scopeUpdated: "Scope updated",
  },
} as const;
